import { useState, type FormEvent } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Trans, useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/auth/auth-layout";
import { SocialButtons } from "@/components/auth/social-buttons";
import { PasswordField } from "@/components/auth/password-field";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";

export const Route = createFileRoute("/_auth/login")({
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation("auth");
  const { t: tErrors } = useTranslation("errors");
  const { redirect } = Route.useSearch();
  const config = useQuery(api.users.authConfig);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    const result = await authClient.signIn.email({ email: email.trim(), password });
    // On success the session appears and the layout route redirects.
    if (result.error) {
      setError(authErrorMessage(result.error, tErrors));
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title={t("login.title")}
      footer={
        <Trans
          t={t}
          i18nKey="login.noAccount"
          components={{
            a: (
              <Link
                to="/signup"
                search={{ redirect }}
                className="text-foreground underline decoration-border-strong underline-offset-4 hover:decoration-foreground"
              />
            ),
          }}
        />
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t("fields.email")} htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <PasswordField
          id="password"
          label={t("fields.password")}
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
        />
        <FormError>{error}</FormError>
        <Button type="submit" disabled={pending}>
          {pending ? <Spinner /> : null}
          {t("login.submit")}
        </Button>
        <Link
          to="/forgot-password"
          search={{ redirect }}
          className="w-fit text-muted-foreground underline decoration-border-strong underline-offset-4 hover:text-foreground"
        >
          {t("login.forgot")}
        </Link>
      </form>
      <SocialButtons
        config={config}
        destination={redirect ?? "/"}
        onError={(failure) => setError(authErrorMessage(failure, tErrors))}
      />
    </AuthLayout>
  );
}
